import { useCallback, useEffect, useMemo, useState } from 'react';

import { NotificationManager, ResponsibleApi, useLabel } from '@atlas/core';

const UNIT_LABELS = {
  HOUR: 'reality_check_custom_hours',
  MINUTE: 'reality_check_custom_min',
};

const useOptionText = (option, customHours, customMinutes) => {
  const hoursLabel = useLabel(UNIT_LABELS.HOUR);
  const minutesLabel = useLabel(UNIT_LABELS.MINUTE);
  const customTimeLabel = useLabel('reality_check_custom_time');

  return useMemo(() => {
    if (!option) return '';
    if (option === 'CUSTOM') {
      const parts = [];
      if (customHours) parts.push(`${customHours} ${hoursLabel}`);
      if (customMinutes) parts.push(`${customMinutes} ${minutesLabel}`);

      return parts.length ? parts.join(' ') : customTimeLabel;
    }

    const [unit, value] = option.split('_');

    return `${value} ${unit === 'HOUR' ? hoursLabel : minutesLabel}`;
  }, [option, customHours, customMinutes, hoursLabel, minutesLabel, customTimeLabel]);
};

const useRealityCheckSettings = ({ onSaveSuccess } = {}) => {
  const [options, setOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [customHours, setCustomHours] = useState('');
  const [customMinutes, setCustomMinutes] = useState('');
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [initialValues, setInitialValues] = useState(null);

  const periodText = useOptionText(selectedOption, customHours, customMinutes);

  const isDirty = (() => {
    if (!initialValues) return false;
    if (selectedOption !== initialValues.option) return true;
    if (selectedOption === 'CUSTOM') {
      return (
        customHours !== initialValues.customHours || customMinutes !== initialValues.customMinutes
      );
    }
    return false;
  })();

  const isButtonDisabled =
    !selectedOption || selectedOption === 'NEVER' || loading || errors.length > 0 || !isDirty;

  const onSaveHandler = useCallback(() => {
    if (!selectedOption) return;
    const minutes =
      selectedOption === 'CUSTOM' ? Number(customHours) * 60 + Number(customMinutes) : null;

    if (selectedOption === 'CUSTOM' && minutes === 0) {
      setErrors([
        {
          message: 'reality_check_please_fill_in',
          type: 'time_interval_greater_than_zero',
          violationInfo: {},
        },
      ]);
      return;
    }

    setErrors([]);
    setLoading(true);
    ResponsibleApi.setRealityCheckSettings({
      minutes,
      period: selectedOption,
    })
      .then(() => {
        NotificationManager.notify({
          data: { period: periodText },
          message: 'reality_check_activated',
          status: 'success',
          type: 'notification',
        });
        setInitialValues({
          customHours: selectedOption === 'CUSTOM' ? customHours : '',
          customMinutes: selectedOption === 'CUSTOM' ? customMinutes : '',
          option: selectedOption,
        });
        onSaveSuccess?.();
      })
      .catch(() => {
        NotificationManager.notify({
          message: 'reality_check_error_message',
          status: 'error',
          type: 'notification',
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [selectedOption, customHours, customMinutes, periodText, onSaveSuccess]);

  const onHoursChange = useCallback(
    e => {
      const val = e.target.value;
      if (/^(0|[1-9]\d{0,2})?$/.test(val)) {
        setCustomHours(val);
        setErrors([]);
        if (val && !customMinutes) setCustomMinutes('0');
      }
    },
    [customMinutes],
  );

  const onMinutesChange = useCallback(e => {
    const val = e.target.value;
    if (/^(0|[1-9]\d?)?$/.test(val)) {
      setCustomMinutes(val);
      setErrors([]);
    }
  }, []);

  const onMinutesBlur = useCallback(() => {
    const val = Number(customMinutes);
    if (val > 59) setCustomMinutes('59');
  }, [customMinutes]);

  const timeOptions = useMemo(
    () =>
      options
        .filter(option => option !== 'NEVER')
        // "CUSTOM" as last element
        .sort((a, b) => (a === 'CUSTOM') - (b === 'CUSTOM')),
    [options],
  );

  useEffect(() => {
    ResponsibleApi.getRealityCheckSettings()
      .then(data => {
        setOptions(data.options);
        const period = data.options.includes(data.period) ? data.period : null;
        setSelectedOption(period);

        let initHours = '';
        let initMinutes = '';

        if (data.period === 'CUSTOM' && data.options.includes('CUSTOM') && data.minutes != null) {
          const hours = Math.floor(data.minutes / 60);
          const mins = data.minutes % 60;
          initHours = hours ? String(hours) : '';
          initMinutes = String(mins || (hours ? 0 : ''));
          setCustomHours(initHours);
          setCustomMinutes(initMinutes);
        }

        setInitialValues({ customHours: initHours, customMinutes: initMinutes, option: period });
      })
      .catch(() => {
        NotificationManager.notify({
          message: 'reality_check_error_message',
          status: 'error',
          type: 'notification',
        });
      })
      .finally(() => {
        setInitialized(true);
      });
  }, []);

  return {
    customHours,
    customMinutes,
    errors,
    initialized,
    isButtonDisabled,
    loading,
    onHoursChange,
    onMinutesBlur,
    onMinutesChange,
    onSaveHandler,
    selectedOption,
    setErrors,
    setSelectedOption,
    timeOptions,
  };
};

export default useRealityCheckSettings;
